import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AdminShadowJob, useAdminStore } from '../stores/adminStore';
import { Button } from '../components/common/Button';
import AdminJobDetailsModal from '../components/admin/modals/AdminJobDetailsModal';
import AdminEditJobModal from '../components/admin/modals/AdminEditJobModal';
import AdminUnpublishJobModal from '../components/admin/modals/AdminUnpublishJobModal';
import AdminPublishJobModal from '../components/admin/modals/AdminPublishJobModal';
import AdminAssignJobToRecruiterModal from '../components/admin/modals/AdminAssignJobToRecruiterModal';
import FilterJobBar from '../components/common/FilterJobBar';
import JobTable from '../components/jobs/JobTable';
import UpdateJobStatusModal from '../components/jobs/UpdateJobStatusModal';
import { useLocation, useNavigate } from 'react-router-dom';
import ShadowRecruiterManagerModal from '../components/admin/ShadowRecruiterManagerModal';
import CreateJobModal from '../components/jobs/CreateJobModal';

const AdminJobList: React.FC = () => {
  const { t } = useTranslation(['common', 'admin', 'jobs']);

  const location = useLocation();
  const navigate = useNavigate();

  const { jobs, fetchJobs, fetchJobById, fetchMoreJobs, fetchShadowRecruiters, hasMore } =
    useAdminStore();
  const [filters, setLocalFilters] = useState({
    status: '',
    visibility: '',
    search: '',
    publication: '',
  });

  const [selectedJob, setSelectedJob] = useState<AdminShadowJob | null>(null);
  const [selectedShadowRecruiterJob, setSelectedShadowRecruiterJob] =
    useState<AdminShadowJob | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showShadowRecruiterManager, setShowShadowRecruiterManager] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showAssignRecruiterModal, setShowAssignRecruiterModal] = useState(false);
  const [showCreateJobModal, setShowCreateJobModal] = useState(false);

  useEffect(() => {
    const state = (location.state || {}) as { selected_id?: string };
    if (state?.selected_id) {
      const selectedId = String(state.selected_id);
      const found = jobs.find(j => j.id === selectedId);

      if (found) {
        setSelectedJob(found);
        setIsModalOpen(true);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, jobs, navigate]);

  useEffect(() => {
    setLoading(true);
    fetchJobs({
      filters,
      offsetCount: 0,
      isLoadMore: false,
    }).finally(() => setLoading(false));
  }, [filters, fetchJobs]);

  useEffect(() => {
    void fetchShadowRecruiters();
  }, [fetchShadowRecruiters]);

  const handleFilterChange = (name: keyof typeof filters, value: string) => {
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleRowClick = (job: AdminShadowJob) => {
    setSelectedJob(job);
    setIsModalOpen(true);
  };

  const handleManageShadowRecruiter = (job?: AdminShadowJob | null) => {
    setSelectedShadowRecruiterJob(job ?? null);
    setShowShadowRecruiterManager(true);
  };

  const handleLoadMore = async () => {
    setLoading(true);
    await fetchMoreJobs();
    setLoading(false);
  };

  const handleJobCreated = async (jobId: string) => {
    // The job itself is already created by the time this fires (CreateJobModal only
    // calls onJobCreated after its upsertJob RPC succeeds), so this success is real
    // regardless of whether the refresh/select-details work below succeeds.
    setShowCreateJobModal(false);
    toast.success(t('jobModeration.createSuccess', { ns: 'admin' }));

    try {
      // fetchJobs resolves to false (rather than rejecting) on failure, so it must be
      // checked explicitly here rather than relying on a caught rejection.
      const refreshed = await fetchJobs({ filters, offsetCount: 0, isLoadMore: false });
      if (!refreshed) {
        throw new Error('Failed to refresh the admin job list after creation');
      }

      // Fetch the created job directly by id rather than looking it up in the
      // (possibly filtered/paginated) refreshed list, so the details modal opens
      // reliably even if the admin's active filters would otherwise exclude it.
      const createdJob = await fetchJobById(jobId);
      if (createdJob) {
        setSelectedJob(createdJob);
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error('Failed to refresh admin job list after creation:', error);
      toast.error(t('jobModeration.createRefreshError', { ns: 'admin' }));
    }
  };

  return (
    <>
      <div className="w-full pt-4 pb-5 flex flex-col md:w-2/3 md:px-1 md:pr-8 md:pb-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <h1 className="text-5xl font-bold hyphens-auto">
            {t('breadcrumbs.jobs', { ns: 'common', defaultValue: "Offre d'emploi" })}
          </h1>
          <Button
            variant="alternate"
            type="button"
            onClick={() => handleManageShadowRecruiter(null)}
            className="md:self-start"
          >
            {t('shadowRecruiters.manage', {
              ns: 'admin',
              defaultValue: 'Manage shadow recruiters',
            })}
          </Button>
          <Button
            type="button"
            onClick={() => setShowCreateJobModal(true)}
            className="md:self-start"
          >
            {t('jobModeration.createButton', { ns: 'admin' })}
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl bg-white border border-gray-300 shadow-sm dark:border-white/[0.08] dark:bg-white/[0.03] transition-all">
        <FilterJobBar filters={filters} onFilterChange={handleFilterChange} />

        <div>
          <JobTable
            jobs={jobs}
            onRowClick={handleRowClick}
            showAdminActivityHubLink
            showShadowRecruiterColumn
          />
        </div>

        {hasMore?.jobs && (
          <div className="py-4 flex justify-center">
            <Button className="px-8" onClick={handleLoadMore} disabled={loading}>
              {loading
                ? t('loading', { ns: 'common', defaultValue: 'Chargement...' })
                : t('loadMore', { ns: 'common', defaultValue: 'Charger plus' })}
            </Button>
          </div>
        )}
      </div>

      {isModalOpen && selectedJob && (
        <AdminJobDetailsModal
          onClose={() => setIsModalOpen(false)}
          job={selectedJob}
          onClick={() => setShowUpdateStatusModal(true)}
          onManageShadowRecruiter={handleManageShadowRecruiter}
          onEdit={() => setShowEditModal(true)}
          onUnpublish={() => setShowUnpublishModal(true)}
          onPublish={() => setShowPublishModal(true)}
          onAssignToRecruiter={() => setShowAssignRecruiterModal(true)}
          onJobFieldUpdated={patch => setSelectedJob(prev => (prev ? { ...prev, ...patch } : prev))}
        />
      )}

      {showEditModal && selectedJob && (
        <AdminEditJobModal
          job={selectedJob}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setIsModalOpen(false);
            setShowEditModal(false);
          }}
        />
      )}

      {showUnpublishModal && selectedJob && (
        <AdminUnpublishJobModal
          job={selectedJob}
          onClose={() => setShowUnpublishModal(false)}
          onSuccess={() => {
            setIsModalOpen(false);
            setShowUnpublishModal(false);
          }}
        />
      )}

      {showPublishModal && selectedJob && (
        <AdminPublishJobModal
          job={selectedJob}
          onClose={() => setShowPublishModal(false)}
          onSuccess={() => {
            setIsModalOpen(false);
            setShowPublishModal(false);
          }}
        />
      )}

      {showAssignRecruiterModal && selectedJob && (
        <AdminAssignJobToRecruiterModal
          job={selectedJob}
          onClose={() => setShowAssignRecruiterModal(false)}
          onSuccess={() => {
            setIsModalOpen(false);
            setShowAssignRecruiterModal(false);
          }}
        />
      )}

      {showUpdateStatusModal && selectedJob && (
        <UpdateJobStatusModal job={selectedJob} onClose={() => setShowUpdateStatusModal(false)} />
      )}

      {showShadowRecruiterManager && (
        <ShadowRecruiterManagerModal
          selectedJob={selectedShadowRecruiterJob}
          onClose={() => {
            setShowShadowRecruiterManager(false);
            setSelectedShadowRecruiterJob(null);
          }}
        />
      )}

      {showCreateJobModal && (
        <CreateJobModal
          onJobCreated={handleJobCreated}
          onClose={() => setShowCreateJobModal(false)}
        />
      )}
    </>
  );
};

export default AdminJobList;
